import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import CustomTypography from "../../../Components/Typography";
import CustomMenu from "../../../Components/CustomMenu";
import { chipStyle, rightSectionStyle } from "../styles/styles";
import SingleServiceAccordion from "../../../Components/SingleServiceAccordian";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";
import { useTranslation } from "react-i18next";
import CustomCheckboxLabel from "../../../Components/CustomCheckboxLabel";
import { sortFullPricelistHierarchy } from "../../../Utils/SortData";
import {
  hasPricedVariants,
  isRenderablePricelist,
  subPricelistHasPricedServices,
} from "../Helpers/pricelistContent";

const PricelistsInfo = (props) => {
  const {
    pricelists,
    viewType,
    selectedPricelists,
    handlePricelistToggle,
    expandedAccordion,
    handleAccordionChange,
    tableConfig,
  } = props;

  const sortedPricelists = sortFullPricelistHierarchy(pricelists);

  const { t, i18n } = useTranslation(["pricelist-info", "categories"]);

  const currentLang = i18n.resolvedLanguage;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showUnoffered, setShowUnoffered] = useState(false);

  // Shared with the modal, which uses the same test to decide whether the
  // Services step exists at all — see Helpers/pricelistContent.
  const filteredPricelists = sortedPricelists.filter(isRenderablePricelist);

  const offeredPricelists =
    viewType === "viewAsCommunityManager"
      ? filteredPricelists
      : filteredPricelists.filter((p) => p.is_offered);

  const unofferedPricelists =
    viewType === "viewAsCommunityManager"
      ? []
      : filteredPricelists.filter((p) => !p.is_offered);

  const renderPricelistSection = (pricelistsToRender = [], isOffered = true) =>
    pricelistsToRender.map((pricelist, idx, arr) => {
      const {
        subcategory_id,
        subcategory_name,
        visit_price,
        visit_price_relevant,
        currency,
        years_of_experience,
        additional_info,
        discount_rate,
        sub_pricelists = [],
      } = pricelist;

      const { files } = additional_info || {};
      const currencySymbol = getCurrencySymbol(currency);

      const validSubPricelists = (sub_pricelists || []).filter(
        subPricelistHasPricedServices
      );

      return (
        <Box key={subcategory_id ?? idx}>
          <Box
            display="flex"
            justifyContent="space-between"
            gap="16px"
            mb="12px"
            sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
          >
            <Box
              display="flex"
              sx={{
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: "4px", sm: "16px" },
              }}
            >
              <Box display="flex" alignItems="center">
                {viewType !== "invite" &&
                  viewType !== "cancel" &&
                  viewType !== "viewAsCommunityManager" && (
                    <CustomCheckboxLabel
                      checked={selectedPricelists.includes(subcategory_id)}
                      onChange={handlePricelistToggle(subcategory_id)}
                      disabled={!isOffered}
                      size="16px"
                      dark={true}
                      formControlLabelStyle={{
                        ...(isMobile && { marginLeft: "-8px" }),
                      }}
                      icon={
                        !isOffered && (
                          <img
                            src="/icons/checked-icons/checkbox-indeterminate-icon.svg"
                            alt="checkbox-indeterminate-icon"
                            width="16px"
                            height="16px"
                          />
                        )
                      }
                      checkedIcon={
                        !isOffered && (
                          <img
                            src="/icons/checked-icons/checkbox-indeterminate-icon.svg"
                            alt="checkbox-indeterminate-icon"
                            width="16px"
                            height="16px"
                          />
                        )
                      }
                    />
                  )}

                <CustomTypography
                  props={{
                    color: "#2F2B3D",
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "28px",
                  }}
                >
                  {t(subcategory_name, { ns: "categories" })}
                </CustomTypography>

                {viewType !== "viewAsCommunityManager" &&
                  viewType !== "invite" &&
                  viewType !== "cancel" && (
                    <Chip
                      label={`${t("discount_offered")}: ${Math.round(
                        discount_rate ?? 0
                      )}%`}
                      sx={{
                        ...chipStyle,
                        height: "24px",
                        fontSize: "13px",
                        ml: "12px",
                      }}
                    />
                  )}
              </Box>

              {years_of_experience || visit_price_relevant ? (
                <Box display="flex" alignItems="center" gap="16px">
                  {years_of_experience !== null ? (
                    <Box display="flex" alignItems="center" gap="6px">
                      <img src="/experience_icon.svg" alt="experience_icon" />
                      <CustomTypography
                        props={{
                          color: "#3E3F4C",
                          fontWeight: 500,
                          fontSize: "14px",
                        }}
                      >
                        {years_of_experience}
                        {currentLang === "en" ? "" : " "}
                        {t("years_experience")}
                      </CustomTypography>
                    </Box>
                  ) : (
                    <></>
                  )}
                  {visit_price !== null &&
                  visit_price !== undefined &&
                  visit_price_relevant ? (
                    <Box display="flex" alignItems="center" gap="6px">
                      <img src="/tag_price_icon.svg" alt="tag_price_icon" />
                      <CustomTypography
                        props={{
                          color: "#3E3F4C",
                          fontWeight: 500,
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {currencySymbol}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {visit_price} {t("visit_fee")}
                        </Box>
                      </CustomTypography>
                    </Box>
                  ) : (
                    <></>
                  )}
                </Box>
              ) : (
                <></>
              )}
            </Box>

            {files?.length > 0 && (
              <CustomMenu
                menuBtnText={!isSmallScreen && t("files_certificates")}
                menuBtnIconPath={
                  isSmallScreen ? "/menu_icon.svg" : "/file_icon.svg"
                }
                menuItems={files.map((file) => ({
                  iconPath: "/file_download_icon.svg",
                  text: file.name,
                  onClick: () =>
                    window.open(file.url, "_blank", "noopener,noreferrer"),
                }))}
              />
            )}
          </Box>

          <Box display="flex" flexDirection="column" gap="6px">
            {validSubPricelists.map((sub) => {
              const { services = [] } = sub;

              const filteredServices = services.filter(hasPricedVariants);
              if (filteredServices.length === 0) return null;

              return filteredServices.map((service) => (
                <SingleServiceAccordion
                  key={service.id}
                  {...{
                    service,
                    isOffered,
                    viewType,
                    expandedAccordion,
                    handleAccordionChange,
                    tableConfig,
                    currencySymbol,
                  }}
                />
              ));
            })}
          </Box>

          {idx !== arr.length - 1 && (
            <Box
              sx={{
                mt: "32px",
                mb:
                  viewType === "invite" || viewType === "cancel"
                    ? "32px"
                    : "0px",
              }}
            >
              <Divider
                sx={{
                  borderColor: "#EAEAEA",
                  borderBottomWidth:
                    viewType === "invite" || viewType === "cancel"
                      ? "1px"
                      : "0px",
                }}
              />
            </Box>
          )}
        </Box>
      );
    });

  return (
    <Box sx={rightSectionStyle}>
      {renderPricelistSection(offeredPricelists, true)}

      {unofferedPricelists.length > 0 && (
        <Box mt={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            sx={{ cursor: "pointer", pr: "16px" }}
            onClick={() => setShowUnoffered((prev) => !prev)}
          >
            <CustomTypography
              props={{
                fontWeight: 500,
                fontSize: "16px",
                color: "#2F2B3D",
                lineHeight: "28px",
              }}
            >
              {t("not_in_offer")}
            </CustomTypography>

            <ExpandMore
              sx={{
                transform: showUnoffered ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
              }}
            />
          </Box>

          <Collapse in={showUnoffered} timeout={300}>
            <Box mt="12px">
              {renderPricelistSection(unofferedPricelists, false)}
            </Box>
          </Collapse>
        </Box>
      )}

      {viewType === "viewAsCommunityManager" &&
      offeredPricelists.length === 0 ? (
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "16px",
              color: "#2F2B3D",
              lineHeight: "28px",
            }}
          >
            {t("create_pricelist_and_sell")}
          </Typography>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default PricelistsInfo;
